import { TimesheetBalanceFragment } from 'generated/resources/graphql'
import _ from 'lodash'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

type AttendanceBalance = TimesheetBalanceFragment
type AttendanceBalancePool = TimesheetBalanceFragment['balancePools'][number]
type AttendanceTimeBlock = TimesheetBalanceFragment['balancePools'][number]['transfers'][number]

export interface BankOfHoursMovementContribution {
  text: string
  subtext: string
  minutes: number
}

export interface BankOfHoursMovement {
  id: string
  date: string
  contributions: BankOfHoursMovementContribution[]
  accumulatedMinutes: number
  minutes: number
}

interface TransfersAndUsages {
  transfers: AttendanceTimeBlock[]
  usages: AttendanceTimeBlock[]
}

interface BuildMovementsOptions {
  useComplementaryHoursLabels?: boolean
}

class BankOfHoursUtils {
  static buildMovements(
    bankOfHoursBalances: Pick<AttendanceBalance, 'id' | 'balancePools' | 'date'>[],
    options: BuildMovementsOptions = {}
  ): BankOfHoursMovement[] {
    const { useComplementaryHoursLabels = false } = options
    const today = moment()
    let accumulatedMinutes = 0

    return bankOfHoursBalances
      .map((balance) => {
        const balancePool = balance.balancePools.find((pool) => pool.type === 'balance')

        const contributions = balancePool
          ? [
              this.overhoursContribution(balancePool, useComplementaryHoursLabels),
              ...this.extraHourRulesBankOfHoursContributions(balancePool),
              ...this.payrollCompensationContributions(balancePool),
              ...this.timeoffCompensationContributions(balancePool),
            ].filter((contribution) => contribution.minutes !== 0)
          : []

        const minutes = _.sumBy(contributions, 'minutes')
        accumulatedMinutes += minutes

        return {
          id: balance.id,
          date: balance.date,
          contributions,
          accumulatedMinutes,
          minutes,
        }
      })
      .filter(
        ({ contributions, date }) =>
          _.some(contributions, (contribution) => contribution.minutes !== 0) &&
          // Currently we are force to send "today" to the API to get correct movements.
          // However we don't want to show today's movement so, we are filtering out here those today's movements.
          !moment(date).isSame(today, 'day')
      )
      .reverse()
  }

  private static overhoursContribution(
    balancePool: AttendanceBalancePool,
    useComplementaryHoursLabels: boolean
  ): BankOfHoursMovementContribution {
    const minutes = _.sumBy(balancePool.transfers, (transfer) => {
      if (transfer.timeSettingsCustomTimeRangeCategoryId) return 0

      return Number(transfer.minutes)
    })

    const text = this.getOverhoursContributionText(minutes, useComplementaryHoursLabels)

    return { text, minutes, subtext: '' }
  }

  private static getOverhoursContributionText(
    minutes: number,
    useComplementaryHoursLabels: boolean
  ): string {
    if (useComplementaryHoursLabels) {
      return minutes > 0
        ? i18n.t('clock_in.bank_of_hours.sidepanel.complementary_hours_movement_contribution')
        : i18n.t(
            'clock_in.bank_of_hours.sidepanel.complementary_hours_movement_negative_contribution'
          )
    }

    return minutes > 0
      ? i18n.t('clock_in.bank_of_hours.sidepanel.extra_hours_movement_contribution')
      : i18n.t('clock_in.bank_of_hours.sidepanel.extra_hours_movement_negative_contribution')
  }

  private static extraHourRulesBankOfHoursContributions(
    balancePool: AttendanceBalancePool
  ): BankOfHoursMovementContribution[] {
    const text = i18n.t(
      'clock_in.bank_of_hours.sidepanel.extra_hour_rule_bank_of_hours_contribution'
    )

    const transfersByRuleIds = _(balancePool.transfers)
      .filter((transfer) => !!transfer.timeSettingsCustomTimeRangeCategoryId && !!transfer.name)
      .groupBy((transfer) => transfer.timeSettingsCustomTimeRangeCategoryId)
      .value()

    const usagesByRuleIds = _(balancePool.usages)
      .filter(
        (usage) =>
          !!usage.timeSettingsCustomTimeRangeCategoryId &&
          !!usage.name &&
          usage.sourcePoolType === 'balance'
      )
      .groupBy((usage) => usage.timeSettingsCustomTimeRangeCategoryId)
      .value()

    const ruleIds = _.uniq(Object.keys(transfersByRuleIds).concat(Object.keys(usagesByRuleIds)))

    const transfersUsagesByRuleIds = ruleIds.reduce<Record<string, TransfersAndUsages>>(
      (acc, ruleId) => {
        const transfers = transfersByRuleIds[ruleId] ?? []
        const usages = usagesByRuleIds[ruleId] ?? []
        acc[ruleId] = { transfers, usages }

        return acc
      },
      {}
    )

    return Object.values(transfersUsagesByRuleIds).map(({ transfers, usages }) => {
      // Previously we made sure that we have at least one transfer or usage.
      const first = (_.first(transfers) ?? _.first(usages))! // eslint-disable-line no-type-assertion/no-type-assertion

      //.name is not null because we filtered out those timeblocks.
      const ruleName = first.name! // eslint-disable-line no-type-assertion/no-type-assertion

      const transferMinutes = _.sumBy(transfers, (transfer) => Number(transfer.minutes))
      const usageMinutes = _.sumBy(usages, (usage) => Number(usage.minutes))
      const minutes = transferMinutes - usageMinutes

      const subtext = i18n.t('clock_in.bank_of_hours.sidepanel.subtext_rule', {
        rule_name: ruleName,
      })

      return { text, minutes, subtext }
    })
  }

  private static timeoffCompensationContributions(
    balancePool: AttendanceBalancePool
  ): BankOfHoursMovementContribution[] {
    return _(balancePool.usages)
      .filter((usage) => usage.sourcePoolType === 'timeoff')
      .groupBy((usage) => usage.timeSettingsCustomTimeRangeCategoryId)
      .values()
      .map((usages) => {
        //.first is not null because groupBy always creates hashes of non-empty arrays.
        const firstUsage = _.first(usages)! // eslint-disable-line no-type-assertion/no-type-assertion
        const ruleId = firstUsage.timeSettingsCustomTimeRangeCategoryId
        const ruleName = firstUsage.name

        const subtext =
          ruleId && ruleName
            ? i18n.t('clock_in.bank_of_hours.sidepanel.subtext_rule', { rule_name: ruleName })
            : ''

        const minutes = _.sumBy(usages, (usage) => -1 * Number(usage.minutes))

        const text =
          minutes < 0
            ? i18n.t('clock_in.bank_of_hours.sidepanel.timeoff_compensation_movement_contribution')
            : i18n.t(
                'clock_in.bank_of_hours.sidepanel.timeoff_compensation_negative_movement_contribution'
              )

        return { text, minutes, subtext }
      })
      .value()
  }

  private static payrollCompensationContributions(
    balancePool: AttendanceBalancePool
  ): BankOfHoursMovementContribution[] {
    const text = i18n.t(
      'clock_in.bank_of_hours.sidepanel.payroll_compensation_movement_contribution'
    )

    const sourcePoolTypes = ['payroll', 'virtual_payroll']

    return _(balancePool.usages)
      .filter((usage) => _.includes(sourcePoolTypes, usage.sourcePoolType))
      .groupBy((usage) => usage.timeSettingsCustomTimeRangeCategoryId)
      .values()
      .map((usages) => {
        //.first is not null because groupBy always creates hashes of non-empty arrays.
        const firstUsage = _.first(usages)! // eslint-disable-line no-type-assertion/no-type-assertion
        const ruleId = firstUsage.timeSettingsCustomTimeRangeCategoryId
        const ruleName = firstUsage.name

        const subtext =
          ruleId && ruleName
            ? i18n.t('clock_in.bank_of_hours.sidepanel.subtext_rule', { rule_name: ruleName })
            : ''

        const minutes = _.sumBy(usages, (usage) => -1 * Number(usage.minutes))

        return { text, minutes, subtext }
      })
      .value()
  }
}

export default BankOfHoursUtils
